import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { AirflowDagContext, AirflowDagRun, AirflowDagRunState } from 'api/types/gcDagApiTypes'
import useApi from 'api/useApi'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

import { QueryKey } from '../queryKeys'

/**
 * `POST tasks/gc_dag` is an asynchronous Airflow trigger — the newly-created run
 * does not appear in the run list for a few seconds. During this window we keep
 * showing an optimistic "Running" run so the status doesn't flip back to idle
 * before Airflow registers the real run. Kept short so it only bridges the
 * registration gap, never the (multi-minute) run itself.
 */
const RECENT_TRIGGER_WINDOW_MS = 45_000

/** Sub-key (scoped to the org's dag-runs key) storing the last trigger timestamp. */
const triggeredAtSubKey = (dagRunsQueryKey: readonly unknown[]) =>
  [...dagRunsQueryKey, 'triggered_at'] as const

const buildOptimisticDagRun = (triggeredAt: number): AirflowDagRun => ({
  dag_run_id: `optimistic-${triggeredAt}`,
  dag_id: 'gc_dag',
  start_date: new Date(triggeredAt).toISOString(),
  end_date: null,
  run_type: 'manual',
  state: AirflowDagRunState.Running,
  conf: {},
})

export const useTriggerGcDag = () => {
  const { trigger_gc_dag } = useApi()
  const queryClient = useQueryClient()
  const dagRunsQueryKey = useOrgQueryKey(QueryKey.DagRuns)
  const triggeredAtQueryKey = triggeredAtSubKey(dagRunsQueryKey)

  return useMutation(({ context }: { context: AirflowDagContext }) => trigger_gc_dag(context), {
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dagRunsQueryKey })

      // Snapshot previous value
      const previousDagRuns = queryClient.getQueryData(dagRunsQueryKey)

      // Record the trigger time so the status query keeps showing a running state
      // until Airflow registers the real run (see useFetchGcDagRuns's `select`).
      const triggeredAt = Date.now()
      queryClient.setQueryData(triggeredAtQueryKey, triggeredAt)

      // Optimistically add a running run for immediate feedback.
      const optimisticRun = buildOptimisticDagRun(triggeredAt)
      queryClient.setQueryData(
        dagRunsQueryKey,
        (old: { dag_runs: AirflowDagRun[] } | undefined) => ({
          ...old,
          dag_runs: [optimisticRun, ...(old?.dag_runs ?? [])],
        })
      )

      return { previousDagRuns }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousDagRuns) {
        queryClient.setQueryData(dagRunsQueryKey, context.previousDagRuns)
      }
      // Clear the trigger marker so we don't keep showing a phantom running state.
      // Note: setQueryData(key, undefined) is a no-op in TanStack Query v4, so the
      // marker must be removed from the cache instead.
      queryClient.removeQueries({ queryKey: triggeredAtQueryKey, exact: true })
    },
    onSettled: () => {
      // Refetch to sync with server. The refetch may return before Airflow has
      // registered the run, but `select` re-injects the optimistic run within the
      // recent-trigger window so the status stays put until the real run appears.
      void queryClient.invalidateQueries({ queryKey: dagRunsQueryKey })
    },
  })
}

export const useFetchGcDagRuns = (options?: { refetchInterval?: number }) => {
  const { fetch_gc_dag_runs } = useApi()
  const queryClient = useQueryClient()
  const dagRunsQueryKey = useOrgQueryKey(QueryKey.DagRuns)
  const triggeredAtQueryKey = triggeredAtSubKey(dagRunsQueryKey)

  return useQuery(dagRunsQueryKey, () => fetch_gc_dag_runs(), {
    refetchInterval: options?.refetchInterval,
    // Bridge the async Airflow registration gap: if we recently triggered a run
    // but the server list doesn't yet contain a running run, re-inject the
    // optimistic run so the status doesn't flicker back to idle.
    select: (data) => {
      const triggeredAt = queryClient.getQueryData<number>(triggeredAtQueryKey)
      const hasRunningRun = data.dag_runs.some((run) => run.state === AirflowDagRunState.Running)
      if (hasRunningRun || !triggeredAt || Date.now() - triggeredAt > RECENT_TRIGGER_WINDOW_MS) {
        return data
      }
      return {
        ...data,
        dag_runs: [buildOptimisticDagRun(triggeredAt), ...data.dag_runs],
      }
    },
  })
}

export const useFetchGcDagNextRun = () => {
  const { fetch_gc_dag_next_run } = useApi()
  const nextDagRunQueryKey = useOrgQueryKey(QueryKey.NextDagRun)

  return useQuery(nextDagRunQueryKey, () => fetch_gc_dag_next_run())
}

export const useFetchGcDagRun = (runId: string) => {
  const { fetch_gc_dag_run } = useApi()
  const dagRunsQueryKey = useOrgQueryKey(QueryKey.DagRuns)

  return useQuery([...dagRunsQueryKey, runId], () => fetch_gc_dag_run(runId), {
    enabled: !!runId,
    refetchInterval: 5_000,
  })
}

export const useFetchGcDagRunTasks = (runId: string) => {
  const { fetch_gc_dag_run_tasks } = useApi()
  const dagRunsQueryKey = useOrgQueryKey(QueryKey.DagRuns)

  return useQuery([...dagRunsQueryKey, runId, 'tasks'], () => fetch_gc_dag_run_tasks(runId), {
    enabled: !!runId,
    refetchInterval: 10_000,
  })
}

export const useStopGcDagRun = () => {
  const { stop_gc_dag_run } = useApi()

  return useMutation((runId: string) => stop_gc_dag_run(runId))
}

export const useFetchGcDagUrl = () => {
  const { fetch_gc_dag_url } = useApi()
  const dagUrlQueryKey = useOrgQueryKey(QueryKey.DagUrl)

  return useQuery(dagUrlQueryKey, () => fetch_gc_dag_url())
}

export const useFetchPendingChanges = (options?: { refetchInterval?: number }) => {
  const pendingChangesQueryKey = useOrgQueryKey(QueryKey.PendingChanges)

  const { fetch_pending_changes } = useApi()
  return useQuery(pendingChangesQueryKey, () => fetch_pending_changes(), {
    refetchInterval: options?.refetchInterval,
  })
}
